import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';

const AI_FORGE_URL = import.meta.env.VITE_AI_FORGE_URL || 'https://ai-forge.rumio.world';

/**
 * Mint an apps-platform session token and post it to AI-Forge so it can
 * call the server-side RPAAD endpoints (`/admin/v1/ai/infer`,
 * `/admin/v1/ai/generate-image`, `/admin/v1/ai/stream`, the model registry,
 * etc.) on behalf of the calling tenant admin.
 *
 * This is the ONLY credential AI-Forge receives. We deliberately no longer
 * send raw provider API keys to the popup: the previous `provider-config`
 * message exposed unmasked tenant AND platform-granted keys in the
 * postMessage payload (readable via DevTools). All generation now runs
 * server-side behind this bearer token, so no key ever reaches the browser.
 *
 * The `source` identifier was renamed from `lsg-parent` → `rumio-parent` as
 * part of finishing the platform rebrand. AI-Forge (separate repo) may need
 * to update any code that filters inbound messages on this exact value.
 */
async function sendRumioSession(popup: Window, nonce: string) {
  try {
    const data = await api.post<{ access_token: string; scopes: string[] }>(
      '/admin/ai-forge/session',
      {},
    );
    const forgeOrigin = new URL(AI_FORGE_URL).origin;
    popup.postMessage(
      {
        source: 'rumio-parent',
        type: 'rumio-session',
        nonce,
        accessToken: data.access_token,
        scopes: data.scopes,
        // The Rumio API origin AI-Forge should target. Derived from this
        // window's origin: tenant admin UI lives at e.g. example.com,
        // its API at api.example.com. Falls back to the current origin
        // for local dev where there's no api. subdomain split.
        apiBaseUrl: deriveApiBaseUrl(),
      },
      forgeOrigin,
    );
  } catch {
    // Non-fatal — AI-Forge degrades to direct-provider mode (still
    // works for image-gen / audio / video which haven't migrated yet).
  }
}

function deriveApiBaseUrl(): string {
  const { protocol, hostname, port } = window.location;
  // Tenant convention: admin UI at <domain>, API at api.<domain>. If
  // already on api., use as-is. Otherwise prepend.
  const apiHost = hostname.startsWith('api.') ? hostname : `api.${hostname}`;
  return `${protocol}//${apiHost}${port ? `:${port}` : ''}`;
}

export type AiForgeAssetType = 'image' | 'video' | 'audio' | 'text';

export interface AiForgeAssetPayload {
  assetType: AiForgeAssetType;
  dataUrl?: string;
  downloadUrl?: string;
  mimeType: string;
  sizeBytes: number;
  textContent?: string;
  metadata: {
    prompt: string;
    model: string;
    provider: string;
    width?: number;
    height?: number;
    duration?: number;
    aspectRatio?: string;
    seed?: number;
  };
}

interface AiForgeMessage {
  source: 'ai-forge';
  type: 'asset-selected' | 'picker-ready' | 'picker-closed' | 'branding-kit-complete';
  nonce: string;
  payload?: AiForgeAssetPayload;
  /** branding-kit-complete only: the Rumio-owned brand kit that finished. */
  kitId?: string;
}

interface UseAiForgePopupOptions {
  context: string;
  suggestedPrompt?: string;
  allowedTypes?: AiForgeAssetType[];
  /** Deep-link a specific AI-Forge tool (e.g. 'logo' for the logo designer). */
  tool?: string;
  /** Branding Studio (tool: 'branding'): the brand_kits row the studio works on. */
  kit?: string;
  onAssetReceived: (payload: AiForgeAssetPayload) => void;
  /**
   * Branding Studio handback. Carries only the kit id — Rumio re-reads the
   * kit from its own DB; the message is a refresh nudge, not a data channel.
   */
  onBrandingKitComplete?: (kitId: string) => void;
  onClose?: () => void;
}

export function useAiForgePopup(options: UseAiForgePopupOptions) {
  const {
    context,
    suggestedPrompt,
    allowedTypes = ['image'],
    tool,
    kit,
    onAssetReceived,
    onBrandingKitComplete,
    onClose,
  } = options;
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const nonceRef = useRef('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setIsOpen(false);
    setIsReady(false);
  }, []);

  // Message handler
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin
      const forgeOrigin = new URL(AI_FORGE_URL).origin;
      if (event.origin !== forgeOrigin) return;

      const data = event.data as AiForgeMessage;
      if (data?.source !== 'ai-forge') return;
      if (data.nonce && nonceRef.current && data.nonce !== nonceRef.current) return;

      switch (data.type) {
        case 'picker-ready':
          setIsReady(true);
          if (popupRef.current && !popupRef.current.closed) {
            sendRumioSession(popupRef.current, nonceRef.current);
          }
          break;
        case 'asset-selected':
          if (data.payload) {
            onAssetReceived(data.payload);
          }
          cleanup();
          break;
        case 'branding-kit-complete':
          if (data.kitId) {
            onBrandingKitComplete?.(data.kitId);
          }
          cleanup();
          break;
        case 'picker-closed':
          onClose?.();
          cleanup();
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onAssetReceived, onBrandingKitComplete, onClose, cleanup]);

  const openForge = useCallback(() => {
    // If already open, refocus
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    const nonce = crypto.randomUUID();
    nonceRef.current = nonce;

    const params = new URLSearchParams({
      mode: 'picker',
      types: allowedTypes.join(','),
      context,
      origin: window.location.origin,
      nonce,
    });
    if (suggestedPrompt) params.set('suggested_prompt', suggestedPrompt);
    if (tool) params.set('tool', tool);
    if (kit) params.set('kit', kit);
    // Pass Axiss widget key so AI-Forge can show the support widget for the current site
    const axissKey = import.meta.env.VITE_AXISS_API_KEY;
    if (axissKey) params.set('axiss_key', axissKey);

    const url = `${AI_FORGE_URL}?${params.toString()}`;
    const width = 1200;
    const height = 800;
    const left = Math.max(0, (screen.width - width) / 2);
    const top = Math.max(0, (screen.height - height) / 2);

    const popup = window.open(
      url,
      'ai-forge-picker',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      // Popup was blocked
      setIsOpen(false);
      return;
    }

    popupRef.current = popup;
    setIsOpen(true);
    setIsReady(false);

    // Poll for popup close (fallback)
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        onClose?.();
        cleanup();
      }
    }, 500);
  }, [context, suggestedPrompt, allowedTypes, tool, kit, onClose, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return { openForge, isOpen, isReady };
}
